// Lógica pura de la conexión de Instagram en el front: qué aviso llegó del popup
// y qué decirle a la persona según el código de error. Sin red ni DOM, para
// probarla con vitest. Los efectos viven en `instagramConexion.ts`.
// Ver ADR-2026-09-24-instagram-conexion-oficial.

/**
 * El botón "Publicar en Instagram" de la vista previa del post publicaba por n8n
 * con la conexión vieja (`instagram_data`), que no se usa más y tiene los tokens
 * vencidos: no le andaba a nadie. Queda escondido hasta que publicar pase a la
 * conexión oficial (permiso `instagram_business_content_publish`).
 * Ver ADR-2026-09-24-instagram-conexion-oficial-2.
 */
export const PUBLICAR_EN_INSTAGRAM_VISIBLE = false;

/**
 * El botón de Mi marca y las herramientas del chat. Meta aprobó el acceso Avanzado de
 * `instagram_business_basic` e `instagram_business_manage_insights` (octubre 2026), así que
 * cualquier cuenta profesional puede conectar: queda para todos
 * (ADR-2026-10-28-instagram-para-todos). Publicar NO entra en esto: tiene su propio permiso.
 */
export const INSTAGRAM_OFICIAL_PARA_TODOS = true;

/** ¿Esta persona ve la conexión oficial de Instagram? */
export function veInstagramOficial(isDevTester: boolean | null | undefined): boolean {
  return INSTAGRAM_OFICIAL_PARA_TODOS || isDevTester === true;
}

/** Se dispara en `window` al conectar o desconectar, para que el chat se entere en el momento. */
export const EVENTO_CAMBIO_DE_INSTAGRAM = "atlas:instagram-cambio";

/** Clave donde `ig-conectado.html` deja el aviso por si se perdió el `opener`. */
export const CLAVE_AVISO = "atlas:ig-conectado";

/** Un aviso guardado hace más que esto es de un intento viejo: se ignora. */
export const VIDA_DEL_AVISO_MS = 15 * 60 * 1000;

export type AvisoDeConexion = { claim: string } | { error: string };

/**
 * Lee lo que mandó `ig-conectado.html` (por postMessage o por localStorage).
 * Devuelve null si no es un aviso nuestro, si está vencido o si el claim no
 * tiene la forma esperada.
 */
export function leerAvisoDeConexion(
  data: unknown,
  ahora = Date.now(),
  /** El `type` que manda la página del popup. Los anuncios de Meta usan "ads-conectado". */
  tipo = "ig-conectado",
): AvisoDeConexion | null {
  let d = data;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return null;
    }
  }
  if (!d || typeof d !== "object") return null;
  const a = d as { type?: unknown; claim?: unknown; error?: unknown; t?: unknown };
  if (a.type !== tipo) return null;
  if (typeof a.t === "number" && ahora - a.t > VIDA_DEL_AVISO_MS) return null;
  if (typeof a.error === "string" && a.error) return { error: a.error };
  if (typeof a.claim === "string" && /^[A-Za-z0-9_-]{32,}$/.test(a.claim)) return { claim: a.claim };
  return null;
}

export interface DetalleDelError {
  /** Nombre de la marca donde ya está la cuenta (sólo si es de la misma empresa). */
  marca?: string | null;
  username?: string | null;
}

/** Qué decirle a la persona según el código que devolvió la edge o el popup. */
export function mensajeDeErrorDeInstagram(code: string, det: DetalleDelError = {}): string {
  const cuenta = det.username ? `@${det.username}` : "Esa cuenta de Instagram";
  switch (code) {
    case "cancelado":
      return "Cancelaste la conexión en Instagram. Cuando quieras, probá de nuevo.";
    case "cuenta_personal":
      return "Tu cuenta de Instagram es personal. Para conectarla tiene que ser profesional (de empresa o de creador). Se cambia gratis desde la app de Instagram, en Configuración → Tipo de cuenta y herramientas.";
    case "claim_invalido":
    case "claim_vencido":
    case "tiempo":
      return "La conexión tardó demasiado y se venció. Probá de nuevo.";
    case "otro_usuario":
      return "Esta conexión la empezó otra persona. Iniciala de nuevo desde tu cuenta.";
    case "cuenta_en_otra_marca":
      return det.marca
        ? `${cuenta} ya está conectada a tu marca "${det.marca}". Desconectala ahí primero.`
        : `${cuenta} ya está conectada a otra marca. Desconectala ahí primero.`;
    case "cuenta_en_otra_empresa":
      return `${cuenta} ya está conectada a otra cuenta de Atlas. Si es tuya, desconectala desde esa cuenta primero.`;
    case "marca":
      return "No encontramos la marca. Recargá la página y probá de nuevo.";
    case "sin_sesion":
      return "Tu sesión venció. Volvé a iniciar sesión y probá de nuevo.";
    case "popup_bloqueado":
      return "Tu navegador bloqueó la ventana de Instagram. Permití las ventanas emergentes para Atlas y probá de nuevo.";
    case "ventana_cerrada":
      return "Se cerró la ventana de Instagram antes de terminar. Probá de nuevo.";
    case "sin_configurar":
    case "origen":
      return "La conexión con Instagram todavía no está disponible desde acá.";
    default:
      return "Instagram no terminó de conectar la cuenta. Probá de nuevo en un rato.";
  }
}

/** El error que tira la conexión: `code` estable para decidir, `message` para mostrar. */
export class ErrorDeInstagram extends Error {
  constructor(
    readonly code: string,
    det: DetalleDelError = {},
  ) {
    super(mensajeDeErrorDeInstagram(code, det));
    this.name = "ErrorDeInstagram";
  }
}
